import { clamp } from './math.ts';
import type { Vec2 } from './types.ts';

const COLLINEAR_EPSILON = 1e-7;
const CLEAN_EPSILON = 0.05;

export const TAU = Math.PI * 2;

export function signedArea(points: readonly Vec2[]): number {
  let area = 0;

  for (let i = 0; i < points.length; i += 1) {
    const a = points[i];
    const b = points[(i + 1) % points.length];

    if (!a || !b) {
      continue;
    }

    area += a.x * b.y - b.x * a.y;
  }

  return area / 2;
}

export function polygonBounds(points: readonly Vec2[]): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} | null {
  const first = points[0];

  if (!first) {
    return null;
  }

  let minX = first.x;
  let minY = first.y;
  let maxX = first.x;
  let maxY = first.y;

  for (let i = 1; i < points.length; i += 1) {
    const point = points[i];

    if (!point) {
      continue;
    }

    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  }

  return { minX, minY, maxX, maxY };
}

export function circleHitsAabb(
  center: Vec2,
  radius: number,
  bounds: { minX: number; minY: number; maxX: number; maxY: number },
): boolean {
  if (radius <= 0) {
    return false;
  }

  const x = clamp(center.x, bounds.minX, bounds.maxX);
  const y = clamp(center.y, bounds.minY, bounds.maxY);
  const dx = center.x - x;
  const dy = center.y - y;

  return dx * dx + dy * dy <= radius * radius;
}

export function circleHitsPolygonBounds(
  center: Vec2,
  radius: number,
  points: readonly Vec2[],
): boolean {
  const bounds = polygonBounds(points);
  return bounds !== null && circleHitsAabb(center, radius, bounds);
}

/** Drops consecutive (and closing) duplicates so clip output stays a real polygon. */
export function cleanPolygon(
  points: readonly Vec2[],
  epsilon = CLEAN_EPSILON,
): Vec2[] {
  const cleaned: Vec2[] = [];

  for (const point of points) {
    const last = cleaned.at(-1);

    if (!last || Math.hypot(point.x - last.x, point.y - last.y) > epsilon) {
      cleaned.push({ x: point.x, y: point.y });
    }
  }

  const first = cleaned[0];
  const last = cleaned.at(-1);

  if (
    first &&
    last &&
    cleaned.length >= 2 &&
    Math.hypot(first.x - last.x, first.y - last.y) <= epsilon
  ) {
    cleaned.pop();
  }

  return cleaned;
}

function isLeftOf(point: Vec2, a: Vec2, b: Vec2): boolean {
  return (
    (b.x - a.x) * (point.y - a.y) - (b.y - a.y) * (point.x - a.x) >=
    -COLLINEAR_EPSILON
  );
}

function lineIntersect(p1: Vec2, p2: Vec2, a: Vec2, b: Vec2): Vec2 {
  const dx1 = p2.x - p1.x;
  const dy1 = p2.y - p1.y;
  const dx2 = b.x - a.x;
  const dy2 = b.y - a.y;
  const denom = dx1 * dy2 - dy1 * dx2;

  if (Math.abs(denom) < COLLINEAR_EPSILON) {
    return { x: p2.x, y: p2.y };
  }

  const t = ((a.x - p1.x) * dy2 - (a.y - p1.y) * dx2) / denom;

  return { x: p1.x + t * dx1, y: p1.y + t * dy1 };
}

/**
 * Clips `subject` to the interior of a convex `clip` polygon (CCW). The subject may be concave.
 */
export function sutherlandHodgman(
  subject: readonly Vec2[],
  clip: readonly Vec2[],
): Vec2[] {
  if (subject.length < 3 || clip.length < 3) {
    return [];
  }

  const clipPoly = signedArea(clip) < 0 ? [...clip].reverse() : clip;
  let output = subject.map((point) => ({ x: point.x, y: point.y }));

  for (let i = 0; i < clipPoly.length; i += 1) {
    const a = clipPoly[i];
    const b = clipPoly[(i + 1) % clipPoly.length];

    if (!a || !b || output.length === 0) {
      return [];
    }

    const input = output;
    output = [];
    let prev = input.at(-1);

    if (!prev) {
      return [];
    }

    for (const curr of input) {
      const currIn = isLeftOf(curr, a, b);
      const prevIn = isLeftOf(prev, a, b);

      if (currIn) {
        if (!prevIn) {
          output.push(lineIntersect(prev, curr, a, b));
        }

        output.push(curr);
      } else if (prevIn) {
        output.push(lineIntersect(prev, curr, a, b));
      }

      prev = curr;
    }
  }

  const cleaned = cleanPolygon(output);
  return cleaned.length >= 3 ? cleaned : [];
}

function pointsEqual(a: Vec2, b: Vec2, epsilon = CLEAN_EPSILON): boolean {
  return Math.hypot(a.x - b.x, a.y - b.y) <= epsilon;
}

function isConvex(a: Vec2, b: Vec2, c: Vec2): boolean {
  return (
    (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x) > COLLINEAR_EPSILON
  );
}

function pointInTriangle(point: Vec2, a: Vec2, b: Vec2, c: Vec2): boolean {
  const ab = (b.x - a.x) * (point.y - a.y) - (b.y - a.y) * (point.x - a.x);
  const bc = (c.x - b.x) * (point.y - b.y) - (c.y - b.y) * (point.x - b.x);
  const ca = (a.x - c.x) * (point.y - c.y) - (a.y - c.y) * (point.x - c.x);
  return (
    (ab >= -COLLINEAR_EPSILON &&
      bc >= -COLLINEAR_EPSILON &&
      ca >= -COLLINEAR_EPSILON) ||
    (ab <= COLLINEAR_EPSILON &&
      bc <= COLLINEAR_EPSILON &&
      ca <= COLLINEAR_EPSILON)
  );
}

function isEar(vertices: readonly Vec2[], index: number): boolean {
  const prev = vertices[(index - 1 + vertices.length) % vertices.length];
  const curr = vertices[index];
  const next = vertices[(index + 1) % vertices.length];

  if (!prev || !curr || !next || !isConvex(prev, curr, next)) {
    return false;
  }

  for (let i = 0; i < vertices.length; i += 1) {
    const prevIndex = (index - 1 + vertices.length) % vertices.length;
    const nextIndex = (index + 1) % vertices.length;

    if (i === index || i === prevIndex || i === nextIndex) {
      continue;
    }

    const other = vertices[i];

    if (other && pointInTriangle(other, prev, curr, next)) {
      return false;
    }
  }

  return true;
}

export function ensureCcw(points: readonly Vec2[]): Vec2[] {
  const copy = points.map((point) => ({ x: point.x, y: point.y }));
  return signedArea(copy) < 0 ? copy.reverse() : copy;
}

/** Ear-clip a simple polygon into triangles. Returns `[]` if the clip cannot finish. */
export function triangulatePolygon(
  polygon: readonly Vec2[],
): [Vec2, Vec2, Vec2][] {
  let remaining = ensureCcw(cleanPolygon(polygon));

  if (remaining.length < 3) {
    return [];
  }

  if (remaining.length === 3) {
    const a = remaining[0];
    const b = remaining[1];
    const c = remaining[2];
    return a && b && c ? [[a, b, c]] : [];
  }

  const triangles: [Vec2, Vec2, Vec2][] = [];
  const guard = remaining.length * remaining.length;

  for (let step = 0; step < guard && remaining.length > 3; step += 1) {
    let clipped = false;

    for (let i = 0; i < remaining.length; i += 1) {
      if (!isEar(remaining, i)) {
        continue;
      }

      const prev = remaining[(i - 1 + remaining.length) % remaining.length];
      const curr = remaining[i];
      const next = remaining[(i + 1) % remaining.length];

      if (prev && curr && next) {
        triangles.push([prev, curr, next]);
      }

      remaining = remaining.filter((_, index) => index !== i);
      clipped = true;
      break;
    }

    if (!clipped) {
      return [];
    }
  }

  const a = remaining[0];
  const b = remaining[1];
  const c = remaining[2];

  if (
    a &&
    b &&
    c &&
    !pointsEqual(a, b) &&
    !pointsEqual(b, c) &&
    !pointsEqual(c, a)
  ) {
    triangles.push([a, b, c]);
  }

  return triangles;
}

export function regularPolygon(
  center: Vec2,
  radius: number,
  sides: number,
): Vec2[] {
  const count = Math.max(3, Math.round(sides));
  const points: Vec2[] = [];

  for (let i = 0; i < count; i += 1) {
    const angle = (i / count) * TAU;
    points.push({
      x: center.x + Math.cos(angle) * radius,
      y: center.y + Math.sin(angle) * radius,
    });
  }

  return points;
}

/** Enough sides that a planning circle reads as round without flooding the clipper. */
export function diskSides(radius: number): number {
  return clamp(Math.round(radius / 5), 32, 96);
}

/**
 * Intersection of a (possibly concave) polygon with a disk, as one polygon.
 * The disk is approximated by a regular n-gon so the clip stays convex.
 */
export function clipPolygonToDisk(
  polygon: readonly Vec2[],
  center: Vec2,
  radius: number,
  sides = diskSides(radius),
): Vec2[] {
  if (radius <= 0) {
    return [];
  }

  const cleaned = cleanPolygon(polygon);

  if (cleaned.length < 3 || !circleHitsPolygonBounds(center, radius, cleaned)) {
    return [];
  }

  return sutherlandHodgman(cleaned, regularPolygon(center, radius, sides));
}

export type Triangle = [Vec2, Vec2, Vec2];

const DISK_SPLIT_EPS = 1e-4;

function distSq(a: Vec2, b: Vec2): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

function triangleArea(tri: Triangle): number {
  return Math.abs(signedArea(tri));
}

function trianglesArea(triangles: readonly Triangle[]): number {
  return triangles.reduce((sum, tri) => sum + triangleArea(tri), 0);
}

function nearestHoleIndex(hole: readonly Vec2[], point: Vec2, center: Vec2): number {
  const target = Math.atan2(point.y - center.y, point.x - center.x);
  let best = 0;
  let bestDiff = Number.POSITIVE_INFINITY;

  for (let i = 0; i < hole.length; i += 1) {
    const vertex = hole[i];

    if (!vertex) {
      continue;
    }

    const angle = Math.atan2(vertex.y - center.y, vertex.x - center.x);
    let diff = Math.abs(angle - target);

    if (diff > Math.PI) {
      diff = TAU - diff;
    }

    if (diff < bestDiff) {
      bestDiff = diff;
      best = i;
    }
  }

  return best;
}

function walkHoleCw(hole: readonly Vec2[], from: number, to: number): Vec2[] {
  const chain: Vec2[] = [];
  let i = from;
  chain.push(hole[i]!);

  while (i !== to) {
    i = (i - 1 + hole.length) % hole.length;
    chain.push(hole[i]!);
  }

  return chain;
}

function triangulateTriangleMinusHole(
  tri: Triangle,
  hole: readonly Vec2[],
  center: Vec2,
): Triangle[] {
  const outer = ensureCcw(tri);
  const a = outer[0];
  const b = outer[1];
  const c = outer[2];
  const ring = ensureCcw(hole);

  if (!a || !b || !c || ring.length < 3) {
    return [tri];
  }

  const ia = nearestHoleIndex(ring, a, center);
  const ib = nearestHoleIndex(ring, b, center);
  const ic = nearestHoleIndex(ring, c, center);
  const triangles: Triangle[] = [];

  const add = (polygon: Vec2[]) => {
    triangles.push(...triangulatePolygon(polygon));
  };

  add([a, b, ...walkHoleCw(ring, ib, ia)]);
  add([b, c, ...walkHoleCw(ring, ic, ib)]);
  add([c, a, ...walkHoleCw(ring, ia, ic)]);

  return triangles;
}

function indexOfPoint(polygon: readonly Vec2[], point: Vec2, epsilon = 0.75): number {
  const limit = epsilon * epsilon;

  for (let i = 0; i < polygon.length; i += 1) {
    const vertex = polygon[i];

    if (vertex && distSq(vertex, point) <= limit) {
      return i;
    }
  }

  return -1;
}

function arcOnIntersection(
  intersection: readonly Vec2[],
  from: Vec2,
  to: Vec2,
  triVerts: readonly Vec2[],
): Vec2[] {
  const start = indexOfPoint(intersection, from);
  const end = indexOfPoint(intersection, to);

  if (start < 0 || end < 0 || start === end) {
    return [];
  }

  const n = intersection.length;
  const forward: Vec2[] = [];

  for (let i = (start + 1) % n; i !== end; i = (i + 1) % n) {
    const vertex = intersection[i];

    if (vertex) {
      forward.push(vertex);
    }
  }

  const backward: Vec2[] = [];

  for (let i = (start - 1 + n) % n; i !== end; i = (i - 1 + n) % n) {
    const vertex = intersection[i];

    if (vertex) {
      backward.push(vertex);
    }
  }

  const isTriVert = (point: Vec2) =>
    triVerts.some((vertex) => distSq(vertex, point) < 0.36);

  const forwardHasTri = forward.some(isTriVert);
  const backwardHasTri = backward.some(isTriVert);

  if (forwardHasTri && !backwardHasTri) {
    return backward;
  }

  if (backwardHasTri && !forwardHasTri) {
    return forward;
  }

  return forward.length <= backward.length ? forward : backward;
}

function pointInConvex(point: Vec2, convex: readonly Vec2[]): boolean {
  if (convex.length < 3) {
    return false;
  }

  for (let i = 0; i < convex.length; i += 1) {
    const a = convex[i];
    const b = convex[(i + 1) % convex.length];

    if (!a || !b || !isLeftOf(point, a, b)) {
      return false;
    }
  }

  return true;
}

function segmentConvexIntersections(
  p: Vec2,
  q: Vec2,
  convex: readonly Vec2[],
): Vec2[] {
  const hits: { t: number; point: Vec2 }[] = [];
  const dx = q.x - p.x;
  const dy = q.y - p.y;

  for (let i = 0; i < convex.length; i += 1) {
    const a = convex[i];
    const b = convex[(i + 1) % convex.length];

    if (!a || !b) {
      continue;
    }

    const edx = b.x - a.x;
    const edy = b.y - a.y;
    const denom = dx * edy - dy * edx;

    if (Math.abs(denom) < COLLINEAR_EPSILON) {
      continue;
    }

    const t = ((a.x - p.x) * edy - (a.y - p.y) * edx) / denom;
    const u = ((a.x - p.x) * dy - (a.y - p.y) * dx) / denom;

    if (t > DISK_SPLIT_EPS && t < 1 - DISK_SPLIT_EPS && u >= -DISK_SPLIT_EPS && u <= 1 + DISK_SPLIT_EPS) {
      hits.push({ t, point: { x: p.x + t * dx, y: p.y + t * dy } });
    }
  }

  hits.sort((left, right) => left.t - right.t);
  return hits.map((hit) => hit.point);
}

function rotateToOutsideConvex(verts: Vec2[], convex: readonly Vec2[]): Vec2[] {
  const start = verts.findIndex((vertex) => !pointInConvex(vertex, convex));

  if (start <= 0) {
    return verts;
  }

  return [...verts.slice(start), ...verts.slice(0, start)];
}

function convexCentroid(points: readonly Vec2[]): Vec2 {
  let x = 0;
  let y = 0;

  for (const point of points) {
    x += point.x;
    y += point.y;
  }

  const n = Math.max(1, points.length);
  return { x: x / n, y: y / n };
}

function convexInsideTriangle(convex: readonly Vec2[], tri: Triangle): boolean {
  const [a, b, c] = tri;
  return !!a && !!b && !!c && convex.every((point) => pointInTriangle(point, a, b, c));
}

function outsideBiteConvex(
  tri: Triangle,
  convex: readonly Vec2[],
  intersection: readonly Vec2[],
): Triangle[] {
  const verts = rotateToOutsideConvex(ensureCcw(tri), convex);
  const result: Vec2[] = [];
  let lastEntry: Vec2 | null = null;
  let firstExit: Vec2 | null = null;

  for (let i = 0; i < 3; i += 1) {
    const u = verts[i];
    const v = verts[(i + 1) % 3];

    if (!u || !v) {
      continue;
    }

    const uIn = pointInConvex(u, convex);
    const vIn = pointInConvex(v, convex);
    const hits = segmentConvexIntersections(u, v, convex);

    if (!uIn) {
      result.push(u);
    }

    if (hits.length === 0) {
      continue;
    }

    if (!uIn && vIn) {
      const hit = hits[0]!;
      result.push(hit);
      lastEntry = hit;
    } else if (uIn && !vIn) {
      const hit = hits[0]!;

      if (lastEntry) {
        result.push(...arcOnIntersection(intersection, lastEntry, hit, verts));
      } else {
        firstExit = hit;
      }

      result.push(hit);
      lastEntry = null;
    } else if (!uIn && !vIn && hits.length >= 2) {
      result.push(hits[0]!);
      result.push(...arcOnIntersection(intersection, hits[0]!, hits[1]!, verts));
      result.push(hits[1]!);
    } else if (uIn && vIn && hits.length >= 2) {
      result.push(hits[0]!);
      result.push(...arcOnIntersection(intersection, hits[0]!, hits[1]!, verts));
      result.push(hits[1]!);
    }
  }

  if (lastEntry && firstExit) {
    result.push(...arcOnIntersection(intersection, lastEntry, firstExit, verts));
  }

  const cleaned = cleanPolygon(result);
  return cleaned.length >= 3 ? triangulatePolygon(cleaned) : [];
}

/**
 * Split a triangle against a convex polygon (a waterline ear).
 * `inside` is over the solid; `outside` is open water.
 */
export function splitTriangleByConvex(
  tri: Triangle,
  convex: readonly Vec2[],
): { inside: Triangle[]; outside: Triangle[] } {
  const clip = ensureCcw(convex);

  if (clip.length < 3) {
    return { inside: [], outside: [tri] };
  }

  const bounds = polygonBounds(tri);
  const clipBounds = polygonBounds(clip);

  if (
    !bounds ||
    !clipBounds ||
    bounds.maxX < clipBounds.minX ||
    bounds.minX > clipBounds.maxX ||
    bounds.maxY < clipBounds.minY ||
    bounds.minY > clipBounds.maxY
  ) {
    return { inside: [], outside: [tri] };
  }

  const [a, b, c] = tri;

  if (a && b && c && pointInConvex(a, clip) && pointInConvex(b, clip) && pointInConvex(c, clip)) {
    return { inside: [tri], outside: [] };
  }

  const intersection = sutherlandHodgman(ensureCcw(tri), clip);

  if (intersection.length < 3) {
    return { inside: [], outside: [tri] };
  }

  const inside = triangulatePolygon(intersection);

  if (convexInsideTriangle(clip, tri)) {
    return {
      inside,
      outside: triangulateTriangleMinusHole(tri, intersection, convexCentroid(clip)),
    };
  }

  const outside = outsideBiteConvex(tri, clip, intersection);
  const area = triangleArea(tri);
  const recovered = trianglesArea(inside) + trianglesArea(outside);

  if (area > 1 && Math.abs(recovered - area) > 0.15 * area) {
    return { inside: [tri], outside: [] };
  }

  return { inside, outside };
}

/**
 * Sequential subtract of a (possibly concave) solid: ear-clip it, then split
 * each remaining triangle against those ears. `inside` is over the solid.
 */
export function splitTrianglesBySolid(
  triangles: readonly Triangle[],
  solid: readonly Vec2[],
): { inside: Triangle[]; outside: Triangle[] } {
  const ears = triangulatePolygon(solid);

  if (ears.length === 0) {
    return { inside: [], outside: [...triangles] };
  }

  let outside = [...triangles];
  const inside: Triangle[] = [];

  for (const ear of ears) {
    const nextOutside: Triangle[] = [];

    for (const tri of outside) {
      const split = splitTriangleByConvex(tri, ear);
      inside.push(...split.inside);
      nextOutside.push(...split.outside);
    }

    outside = nextOutside;
  }

  return { inside, outside };
}

export type AngleArc = {
  start: number;
  end: number;
};

export function pointOnCircle(
  center: Vec2,
  radius: number,
  angle: number,
): Vec2 {
  return {
    x: center.x + Math.cos(angle) * radius,
    y: center.y + Math.sin(angle) * radius,
  };
}

function normalizeAngle(angle: number): number {
  return ((angle % TAU) + TAU) % TAU;
}

function unwrapNear(from: number, to: number): number {
  let result = to;

  while (result - from > Math.PI) {
    result -= TAU;
  }

  while (from - result > Math.PI) {
    result += TAU;
  }

  return result;
}

function refineBoundary(
  center: Vec2,
  radius: number,
  waterAngle: number,
  landAngle: number,
  isLand: (point: Vec2) => boolean,
): number {
  let water = waterAngle;
  let land = unwrapNear(water, landAngle);

  for (let step = 0; step < 8; step += 1) {
    const mid = (water + land) / 2;

    if (isLand(pointOnCircle(center, radius, mid))) {
      land = mid;
    } else {
      water = mid;
    }
  }

  return land;
}

function sampleAngle(index: number, count: number): number {
  return (index / count) * TAU;
}

/**
 * Contiguous runs of the circumference that sit on land. Angles follow engine convention
 * (0 at +X) and stay in `[0, 2π)` except a wrap-around run, whose `end` may exceed `2π`.
 */
export function circleArcsWhere(
  center: Vec2,
  radius: number,
  isLand: (point: Vec2) => boolean,
  samples = 128,
): AngleArc[] {
  if (radius <= 0) {
    return [];
  }

  const count = Math.max(8, Math.round(samples));
  const flags: boolean[] = [];

  for (let i = 0; i < count; i += 1) {
    flags.push(isLand(pointOnCircle(center, radius, sampleAngle(i, count))));
  }

  if (flags.every((flag) => !flag)) {
    return [];
  }

  if (flags.every((flag) => flag)) {
    return [{ start: 0, end: TAU }];
  }

  const origin = flags.findIndex((flag) => !flag);
  const arcs: AngleArc[] = [];
  let i = origin;

  do {
    if (!flags[i]) {
      i = (i + 1) % count;
      continue;
    }

    const startIndex = i;

    while (flags[i]) {
      i = (i + 1) % count;

      if (i === startIndex) {
        break;
      }
    }

    const start = refineBoundary(
      center,
      radius,
      sampleAngle((startIndex - 1 + count) % count, count),
      sampleAngle(startIndex, count),
      isLand,
    );
    const end = refineBoundary(
      center,
      radius,
      sampleAngle(i, count),
      sampleAngle((i - 1 + count) % count, count),
      isLand,
    );

    arcs.push({
      start: normalizeAngle(start),
      end:
        normalizeAngle(end) <= normalizeAngle(start)
          ? normalizeAngle(end) + TAU
          : normalizeAngle(end),
    });
  } while (i !== origin);

  return arcs;
}

/** Open-water complement of `circleArcsWhere` land runs; a full circle when nothing is solid. */
export function complementArcs(solid: readonly AngleArc[]): AngleArc[] {
  if (solid.length === 0) {
    return [{ start: 0, end: TAU }];
  }

  const normalized = solid
    .map((arc) => {
      const start = normalizeAngle(arc.start);
      let end = arc.end;

      if (end <= start) {
        end += TAU;
      }

      return { start, end };
    })
    .sort((a, b) => a.start - b.start);

  const merged: AngleArc[] = [];

  for (const arc of normalized) {
    const last = merged.at(-1);

    if (!last || arc.start > last.end) {
      merged.push({ ...arc });
    } else {
      last.end = Math.max(last.end, arc.end);
    }
  }

  const first = merged[0];
  const last = merged.at(-1);

  if (first && last && last.end >= TAU && first.start <= last.end - TAU) {
    first.start = 0;
    last.end = TAU;
  }

  if (
    merged.length === 1 &&
    first &&
    first.start <= 1e-6 &&
    first.end >= TAU - 1e-6
  ) {
    return [];
  }

  const gaps: AngleArc[] = [];
  let cursor = 0;

  for (const arc of merged) {
    if (arc.start > cursor + 1e-6) {
      gaps.push({ start: cursor, end: arc.start });
    }

    cursor = Math.max(cursor, arc.end);
  }

  if (cursor < TAU - 1e-6) {
    gaps.push({ start: cursor, end: TAU });
  }

  return gaps;
}
